import React, { useState } from 'react';
import { View, Text, Modal, Pressable, StyleSheet, ScrollView, Image } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';

interface CoachEditModalProps {
    visible: boolean;
    onClose: () => void;
    currentCoach: any;
    onSave: (newCoach: any) => void;
    campCoaches: any[];
    sessionTitle: string;
}

const CoachEditModal: React.FC<CoachEditModalProps> = ({
    visible,
    onClose,
    currentCoach,
    onSave,
    campCoaches,
    sessionTitle
}) => {
    const [selectedCoach, setSelectedCoach] = useState(currentCoach);

    const handleCoachSelect = (coach: any) => {
        setSelectedCoach(coach);
    };

    const handleSave = () => {
        onSave(selectedCoach);
        onClose();
    };

    const handleCancel = () => {
        setSelectedCoach(currentCoach);
        onClose();
    };

    const getCoachId = (coach: any) => coach?.userId || coach?.id;
    const getCoachName = (coach: any) => 
        coach ? `${coach.firstName || ''} ${coach.lastName || ''}`.trim() || 'Unnamed Coach' : '';
    const getCoachInitials = (coach: any) => {
        if (!coach) return 'C';
        const first = coach.firstName?.[0] || '';
        const last = coach.lastName?.[0] || '';
        return (first + last).toUpperCase() || 'C';
    };

    return (
        <Modal
            animationType="fade"
            transparent={true}
            visible={visible}
            onRequestClose={onClose}
        >
            <View style={styles.centeredView}>
                <View style={styles.modalView}>
                    <View style={styles.header}>
                        <Text style={styles.modalTitle}>Select Coach</Text>
                        <Text style={styles.sessionTitle}>{sessionTitle}</Text>
                        <Pressable onPress={onClose} style={styles.closeButton}>
                            <MaterialCommunityIcons name="close" size={24} color="#666" />
                        </Pressable>
                    </View>

                    <ScrollView style={styles.coachList}>
                        {/* No Coach Option */}
                        <Pressable
                            style={[
                                styles.coachOption,
                                selectedCoach === null && styles.selectedOption
                            ]}
                            onPress={() => handleCoachSelect(null)}
                        >
                            <View style={styles.coachAvatar}>
                                <MaterialCommunityIcons name="account-off" size={24} color="#999" />
                            </View>
                            <View style={styles.coachInfo}>
                                <Text style={styles.coachName}>No Coach</Text>
                                <Text style={styles.coachDetails}>Session will have no assigned coach</Text>
                            </View>
                            {selectedCoach === null && (
                                <MaterialCommunityIcons name="check-circle" size={24} color="#3C8248" />
                            )}
                        </Pressable>

                        {/* Coach Options */}
                        {campCoaches.map((coach, index) => {
                            const coachId = getCoachId(coach);
                            const coachName = getCoachName(coach);
                            const isSelected = getCoachId(selectedCoach) === coachId;
                            
                            return (
                                <Pressable
                                    key={coachId || index}
                                    style={[
                                        styles.coachOption,
                                        isSelected && styles.selectedOption
                                    ]}
                                    onPress={() => handleCoachSelect(coach)}
                                >
                                    {coach.profileImage ? (
                                        <Image source={{ uri: coach.profileImage }} style={styles.coachAvatar} />
                                    ) : (
                                        <View style={[styles.coachAvatar, styles.coachAvatarPlaceholder]}>
                                            <Text style={styles.coachAvatarText}>{getCoachInitials(coach)}</Text>
                                        </View>
                                    )}
                                    <View style={styles.coachInfo}>
                                        <Text style={styles.coachName}>{coachName}</Text>
                                        {coach.email && (
                                            <Text style={styles.coachContact}>
                                                <MaterialCommunityIcons name="email" size={12} color="#666" /> {coach.email}
                                            </Text>
                                        )}
                                        {coach.phone && (
                                            <Text style={styles.coachContact}>
                                                <MaterialCommunityIcons name="phone" size={12} color="#666" /> {coach.phone}
                                            </Text>
                                        )}
                                        {coach.specialties && coach.specialties.length > 0 && (
                                            <Text style={styles.coachDetails}>
                                                {coach.specialties.join(', ')}
                                            </Text>
                                        )}
                                    </View>
                                    {isSelected && (
                                        <MaterialCommunityIcons name="check-circle" size={24} color="#3C8248" />
                                    )}
                                </Pressable>
                            );
                        })}
                    </ScrollView>

                    <View style={styles.buttonContainer}>
                        <Pressable style={styles.cancelButton} onPress={handleCancel}>
                            <Text style={styles.cancelButtonText}>Cancel</Text>
                        </Pressable>
                        <Pressable style={styles.saveButton} onPress={handleSave}>
                            <Text style={styles.saveButtonText}>Save</Text>
                        </Pressable>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    centeredView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    modalView: {
        width: '90%',
        maxWidth: 500,
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 20,
        maxHeight: '80%',
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 2,
        },
        shadowOpacity: 0.25,
        shadowRadius: 4,
        elevation: 5,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
        paddingBottom: 15,
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    modalTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: '#333',
        flex: 1,
    },
    sessionTitle: {
        fontSize: 14,
        color: '#666',
        flex: 2,
        textAlign: 'center',
    },
    closeButton: {
        padding: 5,
    },
    coachList: {
        flex: 1,
        marginBottom: 20,
    },
    coachOption: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 15,
        borderWidth: 1,
        borderColor: '#e0e0e0',
        borderRadius: 12,
        marginBottom: 12,
        backgroundColor: '#f9f9f9',
    },
    selectedOption: {
        borderColor: '#3C8248',
        backgroundColor: '#f0f9f4',
        borderWidth: 2,
    },
    coachAvatar: {
        width: 56,
        height: 56,
        borderRadius: 28,
        marginRight: 16,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#e0e0e0',
    },
    coachAvatarPlaceholder: {
        backgroundColor: '#3C8248',
    },
    coachAvatarText: {
        color: '#fff',
        fontSize: 20,
        fontWeight: '600',
    },
    coachInfo: {
        flex: 1,
    },
    coachName: {
        fontSize: 16,
        fontWeight: '600',
        color: '#333',
        marginBottom: 4,
    },
    coachContact: {
        fontSize: 13,
        color: '#666',
        marginBottom: 3,
        flexDirection: 'row',
        alignItems: 'center',
    },
    coachDetails: {
        fontSize: 12,
        color: '#999',
        fontStyle: 'italic',
        marginTop: 2,
    },
    buttonContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingTop: 15,
        borderTopWidth: 1,
        borderTopColor: '#e0e0e0',
    },
    cancelButton: {
        flex: 1,
        paddingVertical: 12,
        paddingHorizontal: 20,
        borderRadius: 8,
        marginRight: 10,
        backgroundColor: '#f5f5f5',
        alignItems: 'center',
    },
    cancelButtonText: {
        fontSize: 16,
        color: '#666',
        fontWeight: '500',
    },
    saveButton: {
        flex: 1,
        paddingVertical: 12,
        paddingHorizontal: 20,
        borderRadius: 8,
        backgroundColor: '#2C67D6',
        alignItems: 'center',
    },
    saveButtonText: {
        fontSize: 16,
        color: 'white',
        fontWeight: '600',
    },
});

export default CoachEditModal; 